import axios, {
  AxiosError,
  AxiosResponse,
  RawAxiosRequestHeaders,
  ResponseType,
} from 'axios';
import _, { noop } from 'lodash';
import makeCancellablePromise from 'make-cancellable-promise';
import { CacheEntry, CacheFactory } from 'restAPI/CacheFactory';
import { ApiRequestInfo, HTTPMethod } from 'types/apiRequestInfo';
import { getAPICallContext } from 'utils/apiCallContextUtil';
import { generateApiKeySync } from 'utils/apiKeyGenerator';
import { getCsrfToken, resetUserAuthData } from 'utils/authService';
import { isInIframe } from 'utils/iframeUtils';
import { setLocalStorage } from 'utils/localStorageUtils';

/**
 * Notification callback injected at app bootstrap via setupAPIClientNotifications().
 * Kept as a module-level variable so the API layer has no direct import of any UI component.
 */
let notifyError: (message?: string) => void = noop;

/**
 * Call once at app bootstrap (src/index.tsx) to wire up the error-toast side-effect.
 * The callback receives an error message string and is responsible for displaying it.
 *
 * Until this is called, errors are silently swallowed (notifyError defaults to noop).
 */
export function setupAPIClientNotifications(
  onError: (message?: string) => void,
): void {
  notifyError = onError;
}

const APIClient = axios.create();

const StatusCodeToIntercept = [401, 403, 404];

const isIframe = isInIframe();

APIClient.interceptors.request.use((req) => {
  const { params } = req;
  if (!_.isEmpty(params)) {
    _.each(params, (value, key) => {
      if (_.isArray(value)) {
        params[key] = value.toString();
      }
    });
    if (_.isPlainObject(params)) {
      req.params = new URLSearchParams(
        // Filter both undefined and null
        _.omitBy(params, _.isNil),
      );
    } else {
      req.params = params;
    }
  }
  // In axios 1.x `req.headers` is an AxiosHeaders instance, not a plain
  // object. Mutate it in place via set() to preserve existing headers.
  req.headers.set('X-CSRF-TOKEN', getCsrfToken());

  // Inject call-origin context headers so the backend can make caching decisions.
  // x-ac-dashboard-id is omitted entirely (not set to null/empty) for non-DASHBOARD sources.
  const { acSource, acDashboardId } = getAPICallContext(window.location.hash);
  req.headers.set('x-ac-source', acSource);
  if (acDashboardId !== undefined) {
    req.headers.set('x-ac-dashboard-id', acDashboardId);
  }

  return req;
});

APIClient.interceptors.response.use(
  (response) => response,
  (e) => {
    const status = e?.response?.status;
    if (StatusCodeToIntercept.includes(status)) {
      if (status === 401) {
        const hashLocation = window.location.hash.split('#')[1];
        //skip store last hash location for logout and login urls
        if (
          !hashLocation.toLowerCase().includes('logout') &&
          !hashLocation.toLowerCase().includes('login')
        ) {
          setLocalStorage('hashLocation', hashLocation);
        }
        resetUserAuthData();
        if (isIframe) {
          // for Azure extension on invalid credential it redirect to config page. To restrict this we have made this change.
          notifyError('Invalid Credential');
        } else {
          window.location.hash = '#/logout';
        }
      }

      if (status === 403) {
        notifyError(
          "You don't have permission to perform this action. Please contact your admin.",
        );
      }
      if (status === 404) {
        window.location.hash = '#/404';
      }
    }
    throw e;
  },
);

export default APIClient;

export async function failSafe(
  promise: Promise<AxiosResponse>,
  showDefaultMessage = true,
) {
  try {
    const response = await promise;
    return response.data;
  } catch (e) {
    if (axios.isCancel(e)) {
      // Ignore silently
      return;
    }
    console.dir(e);

    // show only AJAX erros here
    const error = e as AxiosError;
    if (error.isAxiosError) {
      const status = error.response?.status;
      // Check if response error is already handled by interceptor
      if (
        status &&
        !StatusCodeToIntercept.includes(status) &&
        showDefaultMessage
      ) {
        if (_.isEmpty(_.get(error, 'response.data.errors'))) {
          showAPIError(error);
        }
      }
    }
    throw e;
  }
}

export const showAPIError = _.throttle(
  function showAPIError(e: AxiosError) {
    const messageLocation = [
      'response.data.message',
      'response.message',
      'data.message',
      'message',
    ];

    let errorMessage;
    messageLocation.every((loc) => {
      const m = _.result(e, loc);
      if (m) {
        errorMessage = m;
        return false;
      }
      return true;
    });
    const status = e.response?.status ?? 0;
    if (status === 504) {
      errorMessage = 'Request Timed Out.';
    } else if (status >= 500) {
      errorMessage = 'Something went wrong. Please try again!';
    }
    notifyError(errorMessage);
  },
  1500,
  { trailing: false },
);

export type CancellableAPIRequest<T> = {
  promise: Promise<T>;
  cancel: () => void;
  /** Request information for API interception and caching (optional for backward compatibility) */
  requestInfo?: ApiRequestInfo;
};

export function makeCancellableAPIRequest<T>(
  method: HTTPMethod,
  url: string,
  options: {
    data?: any;
    params?: any;
    responseType?: ResponseType;
    headers?: RawAxiosRequestHeaders;
    cache?: boolean;
  } = {},
  showDefaultMessage = true,
): CancellableAPIRequest<T> {
  const { cache = false, ...requestOptions } = options;
  if (cache) {
    const key = generateApiKeySync({
      url,
      method,
      data: requestOptions.data,
      params: requestOptions.params,
    });

    let entry = CacheFactory.get<T>(key);
    if (!entry) {
      // Cache miss — fire the real request and store it.
      const req = makeCancellableAPIRequest<T>(
        method,
        url,
        requestOptions,
        showDefaultMessage,
      );
      const newEntry: CacheEntry<T> = {
        state: 'PENDING',
        promise: req.promise,
        subscribers: 0,
        abort: req.cancel,
      };
      req.promise.then(
        () => {
          if (CacheFactory.get(key) === (newEntry as CacheEntry))
            newEntry.state = 'FULFILLED';
        },
        () => {},
      );
      CacheFactory.save(key, newEntry);
      entry = newEntry;
    }
    entry.subscribers += 1;

    const owned = entry;
    let cancelled = false;
    return {
      promise: owned.promise,
      cancel: () => {
        if (cancelled) return;
        cancelled = true;
        if (owned.state === 'FULFILLED') return;
        if (CacheFactory.get(key) !== owned) return;
        owned.subscribers -= 1;
        if (owned.subscribers <= 0) {
          owned.abort();
          CacheFactory.invalidate(key);
        }
      },
      requestInfo: {
        url,
        method,
        data: requestOptions.data,
        params: requestOptions.params,
      },
    };
  }

  const abortController = new AbortController();
  const { promise, cancel: cancelPromise } = makeCancellablePromise(
    APIClient({
      method,
      url,
      data: requestOptions.data,
      params: requestOptions.params,
      signal: abortController.signal,
      responseType: requestOptions.responseType,
      headers: requestOptions.headers,
    }),
  );

  const handledPromise = failSafe(promise, showDefaultMessage);
  return {
    promise: handledPromise,
    cancel: () => {
      /**
       * Here, we are cancelling both API request and the promise.
       * If we only cancel the request, the promise still resolves with undefined and
       * throws error where the cases are not handled.
       * Cancelling the promise will prevent the promise from resolving as
       * we do not need that promise any more after cancelling it
       */
      cancelPromise();
      abortController.abort();
    },
    // Request info for API interception - used for generating unique cache keys for API
    requestInfo: {
      url,
      method,
      data: requestOptions.data,
      params: requestOptions.params,
    },
  };
}

export function getEmptyCancellableRequest<T>(data?: T) {
  return {
    promise: Promise.resolve(data) as Promise<T>,
    cancel: noop,
    requestInfo: {
      url: '',
      method: undefined,
      data: undefined,
      params: undefined,
    },
  };
}

export const EmptyCancellableRequest = {
  promise: Promise.resolve(),
  cancel: noop,
  requestInfo: {
    url: '',
    method: undefined,
    data: undefined,
    params: undefined,
  },
};
